import { WidgetBase } from '@dojo/framework/core/WidgetBase';
import { Store } from '@dojo/framework/stores/Store';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';

import isNumber from 'lodash-es/isNumber';
import { logQuiz } from 'logger';

import { LinkedList } from 'collections';
import { QuizMeta, QuizRPC, InfoRPC, ProgressionSvc, QuestionType, IResult, StartQuiz, Question } from '../interfaces';
import { setCursor, setQuestion, incrCursor, decrCursor, setInreview, setHasQueue } from './state';
import { MCSelectorProperties } from '../questions/mc-selector';
import timesync from 'timesync';
import { QueueLoader, allqueues } from './queueloader';
// import svc from './qfsvc';

import css from '../css/questionfeedback.m.css';
import { recordProgress } from './xapi';

export interface QuestionListProperties {
	quizstore: Store<QuizMeta>;
	svc: ProgressionSvc;
	finalize: () => void;
	startquiz: StartQuiz;
	quiz: QuizRPC;
	info: InfoRPC;
	question: any;
	isaudit: boolean;
	resultid?: string;
	instancekey?: string;
}

const ISCOMPLETE = 1;
const dummyList = new LinkedList<Question>();

export class QuestionList extends WidgetBase<QuestionListProperties> {
	private cursors: number[] = [];
	private loc: number = 0;
	private inreview: boolean = false;
	private instanceId: string | undefined;
	private originalId: string | undefined;
	private detaching: boolean = false;
	private hasReviewed: boolean = false;
	private _propsInited: boolean = false;

	public get loader(): QueueLoader | undefined {
		const { instanceId, detaching } = this;
		const {
			svc,
			quizstore,
			info: { numberofquestions, lastresult },
			isaudit,
			resultid
		} = this.properties;
		if (instanceId !== undefined) {
			if (allqueues.has(instanceId)) {
				return allqueues.get(instanceId);
			}
			if (!detaching) {
				const qloader = new QueueLoader({
					svc,
					quizstore,
					numberofquestions: isaudit ? lastresult.numberofquestions : numberofquestions,
					instanceId,
					useQueue: false,
					resultid
				});
				allqueues.set(instanceId, qloader);
				return qloader;
			}
		}
		return undefined;
	}

	public get queue(): LinkedList<Question> | undefined {
		const { loader } = this;
		return loader && (loader.queue as LinkedList<Question>);
	}

	public stopLoading(): void {
		logQuiz.debug(() => `QuestionList#stopLoading for ${this.instanceId}`);
		const qloader = this.loader;
		if (qloader === undefined || qloader.stoppedloading) {
			return;
		}
		qloader.stopLoading();
	}

	protected onDetach(): void {
		logQuiz.debug(() => `QuestionQueue#onDetach for ${this.originalId}`);
		this.detaching = true;
		const qloader = this.loader;
		const { instanceId } = this;
		qloader && qloader.stopLoading();
		instanceId && allqueues.delete(instanceId);
	}

	protected get_question_array(): Question[] {
		const { queue } = this;
		if (queue) {
			return queue.toArray();
		}
		return [];
	}

	protected has_next_question(): boolean {
		if (this.cursors.length > 0) {
			return this.loc + 1 < this.cursors.length;
		}
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const { queue } = this;
		const cursor = get(path('cursor'));
		return Boolean(queue && cursor + 1 < queue.length);
	}

	private setQuestion() {
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const cursor = get(path('cursor'));
		const queue = this.queue!;
		const question = queue.elementAtIndex(cursor)!;
		if (!question.starttime) {
			question.starttime = timesync.now().toJSON();
		}
		setQuestion(quizstore)({ question });
	}

	protected next_question(): void {
		const { quizstore, quiz, info } = this.properties;
		if (this.has_next_question()) {
			if (this.cursors.length > 0) {
				this.loc += 1;
				const cursor = this.cursors[this.loc];
				setCursor(quizstore)({ cursor });
				recordProgress(quiz, info, cursor);
				this.setQuestion();
			} else {
				incrCursor(quizstore);
				const cursor = quizstore.get(quizstore.path('cursor'));
				recordProgress(quiz, info, cursor);
				this.setQuestion();
			}
		}
	}

	protected has_previous_question(): boolean {
		if (this.cursors.length > 0) {
			return this.loc > 0;
		}
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		const cursor = get(path('cursor'));
		return cursor > 0;
	}

	protected previous_question(): void {
		const { quizstore } = this.properties;
		if (this.has_previous_question()) {
			if (this.cursors.length > 0) {
				this.loc -= 1;
				const cursor = this.cursors[this.loc];
				setCursor(quizstore)({ cursor });
				this.setQuestion();
			} else {
				decrCursor(quizstore);
				this.setQuestion();
			}
		}
	}

	protected show_review(flag: boolean): void {
		const { quizstore } = this.properties;
		this.inreview = flag;
		if (flag) {
			this.hasReviewed = true;
		}
		setInreview(quizstore)({ inreview: flag });
		logQuiz.info(() => `${flag ? 'show' : 'hide'} review`);
		this.invalidate();
	}

	protected finish_quiz(): void {
		const { finalize, svc, isaudit, quizstore } = this.properties;
		setQuestion(quizstore)({ question: undefined });
		if (isaudit) {
			finalize();
		} else {
			svc.finish_quiz().then(() => {
				finalize();
			});
		}
	}

	protected update_results(results: IResult[]): Promise<any> {
		const {
			info: { qrid },
			quiz: { id },
			svc,
			isaudit
		} = this.properties;

		if (isaudit) {
			return Promise.resolve(true);
		}

		results.forEach((result) => {
			result.qrlocator.qrid = qrid;
			result.qrlocator.quiz = id;
		});
		return svc.update_results({ results });
	}

	protected filter_on(name: 'marked' | 'complete' | 'incomplete' | 'all' | number): void {
		const { quizstore } = this.properties;
		if (isNumber(name)) {
			this.cursors = [];
			setCursor(quizstore)({ cursor: name });
			this.setQuestion();
			return;
		}
		if (name === 'all') {
			this.cursors = [];
			setCursor(quizstore)({ cursor: 0 });
			this.setQuestion();
			return;
		}
		const cursors: number[] = [];
		this.queue!.forEach((q) => {
			switch (name) {
				case 'marked':
					if (q.marked) {
						cursors.push(q.cursor);
					}
					break;
				case 'complete':
					if (q.flags[ISCOMPLETE]) {
						cursors.push(q.cursor);
					}
					break;
				case 'incomplete':
					if (!q.flags[ISCOMPLETE]) {
						cursors.push(q.cursor);
					}
					break;
			}
		});
		if (cursors.length > 0) {
			setCursor(quizstore)({ cursor: cursors[0] });
			this.loc = 0;
			this.cursors = cursors;
			this.setQuestion();
		}
	}

	protected check_for(name: 'marked' | 'complete' | 'incomplete' | 'all'): boolean {
		if (name === 'all') {
			return true;
		}
		const { queue = dummyList } = this;
		let flag = false;
		queue.forEach((q) => {
			switch (name) {
				case 'marked':
					flag = flag || !!q.marked;
					break;
				case 'complete':
					flag = flag || q.flags[ISCOMPLETE];
					break;
				case 'incomplete':
					flag = flag || !q.flags[ISCOMPLETE];
					break;
			}
		});
		return flag;
	}

	protected onAttach(): void {
		const { quizstore } = this.properties;
		if (allqueues.size > 0) {
			logQuiz.debug(() => `already have QuestionList ${Array.from(allqueues.keys()).join(', ')}`);
			this.originalId = this.instanceId;
			this.instanceId = '--bad--';
			this.detaching = true;
			return;
		}

		setHasQueue(quizstore)({ hasqueue: true });

		const { startquiz, instancekey, isaudit } = this.properties;
		if (this.instanceId && this.instanceId !== instancekey) {
			logQuiz.debug(() => `QuestionList#onAttach for defunct ${this.originalId}`);
			return;
		}
		this.instanceId = instancekey;
		this.originalId = instancekey;
		logQuiz.debug(() => `QuestionList#onAttach for ${this.instanceId}`);

		const { loader } = this;
		if (loader) {
			loader.load_questions(startquiz, isaudit);
		}
	}

	protected render(): DNode {
		const { instancekey, quizstore } = this.properties;
		const { detaching, instanceId } = this;
		if (detaching || (instanceId && instanceId !== instancekey)) {
			logQuiz.debug(() => `QuestionList#render defunct instance ${this.originalId}`);
			return null;
		}

		const { isaudit } = this.properties;
		const { question } = this.properties;
		if (!question) {
			return v('div', { key: 'spinner', classes: [css.loading] }, [
				v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
			]);
		}

		const { questiontype } = question;
		const key = `${questiontype}-${instancekey}`;
		return w<QuestionType>(questiontype, {
			key,
			quizstore,
			inreview: this.inreview,
			hasReviewed: this.hasReviewed,
			isaudit,
			hasNextQuestion: this.has_next_question,
			onNextQuestion: this.next_question,
			hasPreviousQuestion: this.has_previous_question,
			onPreviousQuestion: this.previous_question,
			onFinish: this.finish_quiz,
			updater: this.update_results,
			onReview: this.show_review,
			getQuestions: this.get_question_array,
			filterOn: this.filter_on,
			checkFor: this.check_for
		} as MCSelectorProperties & WidgetProperties);
	}

	@beforeProperties()
	protected stateProperties(
		properties: QuestionListProperties & WidgetProperties
	): QuestionListProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._propsInited) {
			const paths = [['quiz'], ['info'], ['question'], ['isaudit'], ['instancekey']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			this._propsInited = true;
		}

		properties.quiz = get(path('quiz'));
		properties.info = get(path('info'));
		properties.isaudit = get(path('isaudit'));
		properties.question = get(path('question'));
		properties.instancekey = get(path('instancekey'));
		return properties;
	}
}

export default QuestionList;
